import {
  AloftArea,
  AloftPrice,
  CraftsmanAdjustmentProps,
  DEFAULT_CORNERS,
  PositiveInt,
  QualityAdjustProps,
  getCraftsmanAdjustment,
  isDefined,
  isUndefined,
  schPositiveInt,
} from '@aloft/toolkit-shared';
import { createSelector } from '@reduxjs/toolkit';

import { selectAdjustmentSettings, selectCompSequence } from '../orderSelectors.ts';
import { selectImprovementsEntities } from '../propertyDataSelectors/improvementsSelectors.ts';
import { selectManualAdjustmentsEntities } from '../propertyDataSelectors/manualAdjustmentsSelectors.ts';
import { selectPropertyIdentificationEntities } from '../propertyDataSelectors/propertyIdentificationSelectors.ts';
import { selectSubjectDepreciationRatioForFeature } from '../selectDepreciationRatioForFeature.ts';
import { createPriceSelector } from '../utils/createPriceSelector.ts';
import { selectAdjustmentProps } from './selectAdjustmentProps.ts';
import { applyRoundingAndThresholds } from './utils/applyRoundingAndThresholds.ts';
import { getColorFromAdjustment } from './utils/getColorFromAdjustment.ts';
import { getStringValueFromAdjustment } from './utils/getStringValueFromAdjustment.ts';

const calculateQualityAdj = (
  adjustmentProps: CraftsmanAdjustmentProps,
  improvements: ReturnType<typeof selectImprovementsEntities>,
  propertyIdentification: ReturnType<typeof selectPropertyIdentificationEntities>,
  depreciationRatio: number | undefined,
  compID: string,
) => {
  const subjectImprovements = improvements.subject;
  const compImprovements = improvements[compID];
  const subjPropertyIdentification = propertyIdentification.subject;
  const compPropertyIdentification = propertyIdentification[compID];

  if (
    isUndefined(subjectImprovements) ||
    isUndefined(compImprovements) ||
    isUndefined(subjPropertyIdentification) ||
    isUndefined(compPropertyIdentification) ||
    isUndefined(depreciationRatio)
  ) {
    return undefined;
  }

  const { craftsmanQuality: quality, propertyType: subjPropertyType } = subjectImprovements;
  const {
    gla,
    corners,
    craftsmanQuality: compQuality,
    fullBaths = 0,
    halfBaths = 0,
    basement,
    propertyType: compPropertyType,
  } = compImprovements;
  const {
    address: { PostalCode: subjZipCode },
  } = subjPropertyIdentification;
  const {
    address: { PostalCode: compZipCode },
  } = compPropertyIdentification;

  const MIN_NUMBER_OF_UNITS = schPositiveInt.parse(1);

  let numberOfUnits: PositiveInt;
  if (compPropertyType === 'multiFamily' && compImprovements.numberOfUnits) {
    numberOfUnits = compImprovements.numberOfUnits;
  } else {
    numberOfUnits = MIN_NUMBER_OF_UNITS;
  }

  if (
    isUndefined(compZipCode) ||
    isUndefined(subjZipCode) ||
    isUndefined(gla) ||
    isUndefined(subjPropertyType) ||
    isUndefined(compPropertyType) ||
    isUndefined(quality) ||
    isUndefined(compQuality) ||
    (quality * 10) % 1 !== 0 ||
    quality < 1
  ) {
    return undefined;
  }

  const totalArea = isDefined(basement?.totalArea)
    ? AloftArea.schema.parse(basement?.totalArea)
    : AloftArea.zero;

  const finishedArea = isDefined(basement?.finishedArea)
    ? AloftArea.schema.parse(basement?.finishedArea)
    : AloftArea.zero;

  const adjCalculator = getCraftsmanAdjustment(adjustmentProps);
  const compBasement = {
    totalArea,
    finishedArea,
    quality: compQuality,
    propertyType: compPropertyType,
    zipCode: compZipCode,
  };

  const compFullBaths = schPositiveInt.parse(fullBaths + (basement?.fullBaths ?? 0));
  const compHalfBaths = schPositiveInt.parse(halfBaths + (basement?.halfBaths ?? 0));
  const compBathrooms = {
    fullBaths: compFullBaths,
    halfBaths: compHalfBaths,
    quality: compQuality,
    propertyType: compPropertyType,
    zipCode: compZipCode,
  };

  const subjectProps =
    subjPropertyType === 'manufacturedHome'
      ? ({
          propertyType: 'manufacturedHome',
          quality,
          zipCode: subjZipCode,
          foundation: {
            foundationType: subjectImprovements.foundation,
            sqft: AloftArea.schema.parse(subjectImprovements.gla),
            zipCode: subjZipCode,
          },
        } as QualityAdjustProps['subject'])
      : ({
          propertyType: subjPropertyType,
          quality,
          zipCode: subjZipCode,
        } as QualityAdjustProps['subject']);

  switch (compPropertyType) {
    case 'singleFamilyResidence':
      return adjCalculator.getQualityAdj({
        subject: subjectProps,
        comp: {
          propertyType: compPropertyType,
          dwelling: {
            sqft: AloftArea.schema.parse(gla),
            quality: compQuality,
            zipCode: compZipCode,
            corners: corners ?? DEFAULT_CORNERS,
          },
          basement: compBasement,
          bathrooms: compBathrooms,
        },
        depreciationRatio,
      });
    case 'manufacturedHome':
      return adjCalculator.getQualityAdj({
        subject: subjectProps,
        comp: {
          propertyType: 'manufacturedHome',
          zipCode: compZipCode,
          dwelling: {
            sqft: AloftArea.schema.parse(gla),
            quality: compQuality,
          },
          basement: compBasement,
          bathrooms: compBathrooms,
          foundation: {
            foundationType: compImprovements.foundation ?? 'pierAndBeam',
            sqft: AloftArea.schema.parse(compImprovements.gla),
            zipCode: compZipCode,
          },
        },
        depreciationRatio,
      });
    case 'multiFamily':
      return adjCalculator.getQualityAdj({
        subject: subjectProps,
        comp: {
          propertyType: compPropertyType,
          dwelling: {
            avgUnitSqft: AloftArea.schema.parse({
              ...gla,
              unitAmount: gla.unitAmount / numberOfUnits,
            }),
            zipCode: compZipCode,
            quality: compQuality,
            units: numberOfUnits,
          },
          basement: compBasement,
          bathrooms: compBathrooms,
        },
        depreciationRatio,
      });
    default:
      // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
      throw new Error(`Unknown property type: ${compPropertyType}`);
  }
};

const getQualityAdj = (
  adjustmentProps: CraftsmanAdjustmentProps,
  improvements: ReturnType<typeof selectImprovementsEntities>,
  propertyIdentification: ReturnType<typeof selectPropertyIdentificationEntities>,
  depreciationRatio: number | undefined,
  compID: string,
) => {
  const adjOutput = calculateQualityAdj(
    adjustmentProps,
    improvements,
    propertyIdentification,
    depreciationRatio,
    compID,
  );

  if (adjOutput === undefined) {
    return undefined;
  }

  return adjOutput.adjustment;
};

const selectQualityAdj = createPriceSelector(
  [
    selectAdjustmentProps,
    selectImprovementsEntities,
    selectPropertyIdentificationEntities,
    (state) => selectSubjectDepreciationRatioForFeature(state, undefined),
    (state, id: string) => id,
  ],
  getQualityAdj,
);

export const selectQualityAdjs = createSelector(
  [
    selectAdjustmentProps,
    selectImprovementsEntities,
    selectPropertyIdentificationEntities,
    (state) => selectSubjectDepreciationRatioForFeature(state, undefined),
    selectCompSequence,
  ],
  (adjustmentProps, improvements, propertyIdentification, depreciationRatio, compSequence) =>
    compSequence.reduce((acc: Record<string, AloftPrice>, propID) => {
      const adj = getQualityAdj(
        adjustmentProps,
        improvements,
        propertyIdentification,
        depreciationRatio,
        propID,
      );

      if (isDefined(adj)) acc[propID] = adj;

      return acc;
    }, {}),
);

export const selectQualityAdjsOutput = createSelector(
  [
    selectAdjustmentProps,
    selectImprovementsEntities,
    selectPropertyIdentificationEntities,
    (state) => selectSubjectDepreciationRatioForFeature(state, undefined),
    selectCompSequence,
  ],
  (adjustmentProps, improvements, propertyIdentification, depreciationRatio, compSequence) =>
    ['subject', ...compSequence].reduce(
      (acc: Record<string, ReturnType<typeof calculateQualityAdj>>, propID) => {
        const adjOutput = calculateQualityAdj(
          adjustmentProps,
          improvements,
          propertyIdentification,
          depreciationRatio,
          propID,
        );

        if (isDefined(adjOutput)) acc[propID] = adjOutput;

        return acc;
      },
      {},
    ),
);

const selectAppliedQualityAdj = createPriceSelector(
  [selectQualityAdj, selectAdjustmentSettings],
  (adj, adjSettings) => {
    if (isUndefined(adj)) {
      return undefined;
    }

    return applyRoundingAndThresholds(adj, 'quality', adjSettings);
  },
);

export const selectAppliedQualityAdjDisplay = createSelector([selectAppliedQualityAdj], (adj) => {
  if (isUndefined(adj)) {
    return undefined;
  }

  return {
    value: getStringValueFromAdjustment(adj),
    color: getColorFromAdjustment(adj),
  };
});

export const selectAppliedQualityAdjsDisplay = createSelector(
  [selectQualityAdjs, selectAdjustmentSettings, selectManualAdjustmentsEntities],
  (adjs, adjSettings, manualAdjustmentsEntities) =>
    Object.entries(adjs).reduce(
      (acc: Record<string, { value: string; color: string }>, [id, adj]) => {
        const appliedAdj = applyRoundingAndThresholds(adj, 'quality', adjSettings);
        const manualAdjustment = manualAdjustmentsEntities[id]?.quality;

        if (isDefined(manualAdjustment)) {
          const manualAdjustmentPrice = AloftPrice.schema.parse(manualAdjustment);
          acc[id] = {
            value: `${getStringValueFromAdjustment(manualAdjustmentPrice)}*`,
            color: getColorFromAdjustment(manualAdjustmentPrice),
          };
        } else if (isDefined(appliedAdj)) {
          acc[id] = {
            value: getStringValueFromAdjustment(appliedAdj),
            color: getColorFromAdjustment(appliedAdj),
          };
        }

        return acc;
      },
      {},
    ),
);
